/* eslint-disable no-console */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { enqueueSnackbar } from 'notistack';

import { yupResolver } from '@hookform/resolvers/yup';
import { useConfirmPasswordApi } from '@pages/authorization/api/useConfirmPasswordApi';
import type { AppAxiosResponse } from '@shared/api/baseQueryTypes';
import { UsersApi } from '@shared/api/baseQuerys';
import { RoutePaths } from '@shared/config/routePathsEnum';
import { StatusCode } from '@shared/const/statusCode';
import { ValidationMessages } from '@shared/validations/validation_messages';

import i18n from '../../../i18n';
import { Form, schema } from '../lib/validateConfirm';

export const useConfirmPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const initialExpiration = location.state?.codeExpiration;

  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [codeExpiration, setCodeExpiration] = useState<string | null>(initialExpiration || null);

  // Таймер для активации кнопки "Отправить код повторно"
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setIsResendDisabled(false);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const {
    handleSubmit,
    register,
    control,
    setError,
    formState: {
      errors: { verificationCode },
    },
  } = useForm<Form>({
    resolver: yupResolver(schema) as Resolver<Form>,
    defaultValues: { email },
  });

  const { mutate, isLoading } = useConfirmPasswordApi();

  const handleResendCode = async () => {
    if (!email) {
      enqueueSnackbar(i18n.t('auth.emailNotDetermined'), { variant: 'error' });
      return;
    }

    try {
      const response = await UsersApi.resetPassword({ email });
      enqueueSnackbar(i18n.t('auth.codeResent'), { variant: 'success' });

      // Обновляем время истечения кода из ответа сервера
      setCodeExpiration((response as { data: { data: string } }).data.data);
      setIsResendDisabled(true);
      setSecondsLeft(60);

      // Перезапускаем таймер активации кнопки
      const timer = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            setIsResendDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      enqueueSnackbar(i18n.t('auth.resendFailed'), { variant: 'error' });
    }
  };

  const onSubmit = async (data: Form) => {
    if (!email) {
      enqueueSnackbar(i18n.t('auth.emailNotDetermined'), { variant: 'error' });
      return;
    }

    const cleanedVerificationCode = data.verificationCode.trim().replace(/\s/g, '');

  // Функция для преобразования русской GMT даты в локальное время
  const convertGMTToLocal = (message: string): string => {
    const months = {
      'янв.': 'Jan',
      'янв': 'Jan',
      'фев.': 'Feb',
      'фев': 'Feb',
      'мар.': 'Mar',
      'мар': 'Mar',
      'апр.': 'Apr',
      'апр': 'Apr',
      'мая.': 'May',
      'мая': 'May',
      'мая г.': 'May',
      'июн.': 'Jun',
      'июн': 'Jun',
      'июл.': 'Jul',
      'июл': 'Jul',
      'авг.': 'Aug',
      'авг': 'Aug',
      'сен.': 'Sep',
      'сен': 'Sep',
      'сент.': 'Sep',
      'сент': 'Sep',
      'окт.': 'Oct',
      'окт': 'Oct',
      'ноя.': 'Nov',
      'ноя': 'Nov',
      'дек.': 'Dec',
      'дек': 'Dec',
    };

      try {
        // Ищем дату в формате "16 июн. 2025 г., 08:18:04"
        const dateMatch = message.match(/(\d{1,2}) ([а-яё]+\.) (\d{4}) г\., (\d{2}:\d{2}:\d{2})/i);
        if (!dateMatch) return message;

        const [dateStr] = dateMatch;
        const [day, monthRus, year, , time] = dateStr.split(/[ ,]+/);
        const monthEng = months[monthRus.toLowerCase() as keyof typeof months];
        if (!monthEng) return message;

        // Создаем строку в формате "Jun 16 2025 08:18:04 GMT"
        const gmtDateStr = `${monthEng} ${day} ${year} ${time} GMT`;
        const date = new Date(gmtDateStr);
        if (isNaN(date.getTime())) return message;

        // Форматируем в локальное время с русскими названиями месяцев
        const formatter = new Intl.DateTimeFormat('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

        const localDateStr = formatter.format(date);
        return message.replace(dateMatch[0], localDateStr);
      } catch (e) {
        console.error('Ошибка преобразования даты:', e);
        return message;
      }
    };

    const extractLockUntilLocal = (message: string): string | null => {
      const months = {
        'янв.': 'Jan',
        'янв': 'Jan',
        'фев.': 'Feb',
        'фев': 'Feb',
        'мар.': 'Mar',
        'мар': 'Mar',
        'апр.': 'Apr',
        'апр': 'Apr',
        'мая.': 'May',
        'мая': 'May',
        'мая г.': 'May',
        'июн.': 'Jun',
        'июн': 'Jun',
        'июл.': 'Jul',
        'июл': 'Jul',
        'авг.': 'Aug',
        'авг': 'Aug',
        'сен.': 'Sep',
        'сен': 'Sep',
        'сент.': 'Sep',
        'сент': 'Sep',
        'окт.': 'Oct',
        'окт': 'Oct',
        'ноя.': 'Nov',
        'ноя': 'Nov',
        'дек.': 'Dec',
        'дек': 'Dec',
      };

      try {
        const dateMatch = message.match(/(\d{1,2}) ([а-яё]+\.) (\d{4}) г\., (\d{2}:\d{2}:\d{2})/i);
        if (!dateMatch) return null;
        const [dateStr] = dateMatch;
        const [day, monthRus, year, , time] = dateStr.split(/[ ,]+/);
        const monthEng = months[monthRus.toLowerCase() as keyof typeof months];
        if (!monthEng) return null;
        const date = new Date(`${monthEng} ${day} ${year} ${time} GMT`);
        if (isNaN(date.getTime())) return null;
        return new Intl.DateTimeFormat(i18n.language || 'ru', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(date);
      } catch {
        return null;
      }
    };

    const localizeVerificationError = (rawMessage: string): string => {
      const localizedMessage = convertGMTToLocal(rawMessage);
      const rawLower = String(rawMessage || '').toLowerCase();
      const localizedLower = String(localizedMessage || '').toLowerCase();
      const isAttemptsExceeded =
        rawLower.includes('превышено количество попыток') ||
        rawLower.includes('too many attempts') ||
        rawLower.includes('attempt limit exceeded') ||
        rawLower.includes('временно заблокирован') ||
        rawLower.includes('заблокирована до') ||
        rawLower.includes('процедура смены пароля') ||
        rawLower.includes('password reset is blocked') ||
        rawLower.includes('blocked until') ||
        localizedLower.includes('превышено количество попыток') ||
        localizedLower.includes('too many attempts') ||
        localizedLower.includes('attempt limit exceeded') ||
        localizedLower.includes('временно заблокирован') ||
        localizedLower.includes('заблокирована до') ||
        localizedLower.includes('процедура смены пароля') ||
        localizedLower.includes('password reset is blocked') ||
        localizedLower.includes('blocked until');
      if (!isAttemptsExceeded) return localizedMessage;

      const until = extractLockUntilLocal(rawMessage) ?? extractLockUntilLocal(localizedMessage);
      return i18n.t('auth.confirmCodeAttemptsExceeded', { until: until || '—' });
    };

    const mapVerificationError = (rawMessage: string): string => {
      const base = localizeVerificationError(rawMessage);
      const rawLower = String(rawMessage || '').toLowerCase();
      const baseLower = String(base || '').toLowerCase();
      const isInvalidCode =
        rawLower.includes('invalid verification code') ||
        rawLower.includes('verification code is invalid') ||
        rawLower.includes('неверный код') ||
        rawLower.includes('неверный проверочный код') ||
        rawLower.includes('некорректный код') ||
        rawLower.includes('некорректный проверочный код') ||
        rawLower.includes('invalid confirm code') ||
        rawLower.includes('confirmation code is invalid') ||
        rawLower.includes('код невер') ||
        baseLower.includes('invalid verification code') ||
        baseLower.includes('verification code is invalid') ||
        baseLower.includes('неверный код') ||
        baseLower.includes('неверный проверочный код') ||
        baseLower.includes('некорректный код') ||
        baseLower.includes('некорректный проверочный код') ||
        baseLower.includes('invalid confirm code') ||
        baseLower.includes('confirmation code is invalid') ||
        baseLower.includes('код невер');
      if (isInvalidCode) return i18n.t('auth.confirmCodeInvalid');
      return base;
    };

    mutate(
      {
        email,
        verificationCode: cleanedVerificationCode,
      },
      {
        onSuccess: (response: AppAxiosResponse<{ message?: string }>) => {
          if (response?.status === StatusCode.SUCCESS) {
            enqueueSnackbar(i18n.t('auth.codeConfirmed'), { variant: 'success' });
            navigate(RoutePaths.forgetPassword, {
              state: { email: email },
            });
          } else {
            const errorMessage = response?.detail || ValidationMessages.defaultError;
            const localizedMessage = mapVerificationError(errorMessage);

            setError('verificationCode', {
              type: 'custom',
              message: localizedMessage,
            });
          }
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.detail || ValidationMessages.defaultError;
          const localizedMessage = mapVerificationError(errorMessage);

          setError('verificationCode', {
            type: 'custom',
            message: localizedMessage,
          });
        },
      },
    );
  };

  return {
    handleSubmit: handleSubmit(onSubmit),
    isLoading,
    register,
    errorVerificationCode: verificationCode?.message || '',
    control,
    handleResendCode,
    isResendDisabled,
    secondsLeft,
    codeExpiration, // Добавляем codeExpiration в возвращаемые значения
  };
};
